import { type ReactElement, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { skipToken } from '@reduxjs/toolkit/query';
import Button from 'components/Button/Button';
import EmptyList from 'components/EmptyList/EmptyList';
import { BUTTON_VARIANT } from 'constants/interfaces';
import { useAppDispatch } from 'store/hooks/useApp';
import { useGetAiChatMessagesQuery } from 'store/slices/aiChats/apis/aiChatsApi';
import { watchTowerApi } from 'store/slices/watchTower/apis/watchTowerApi';
import {
  WATCH_TOWER_COMPANIES_TAG,
  WATCH_TOWER_COMPANY_TAG,
  WATCH_TOWER_RUNS_TAG,
} from 'store/tags/watchTower';

import { bindCreateChatAiChatId } from './bindWatchAiChat';
import WatchAiChatDialog from './WatchAiChatDialog';
import { createCompanyChatScreen } from './watchChatScreen';
import {
  CREATE_WATCH_COMPANIES_USER_TURN,
  createWatchCompaniesCommitKind,
  lastCreateWatchCompaniesOutput,
  pendingRowsFromAiChatMessages,
} from './watchCreateChatToolParts';

import type { WatchTowerMockState } from './types';

import styles from './scss/TargetedMarketing.module.scss';

const MESSAGES_LIMIT = 20;

interface IWatchTowerCreateChatDialog {
  open: boolean,
  chatId: number,
  state: WatchTowerMockState,
  onClose: () => void,
  onApply: (next: WatchTowerMockState) => void,
}

function WatchTowerCreateChatDialog({
  open,
  chatId,
  state,
  onClose,
  onApply,
}: IWatchTowerCreateChatDialog): ReactElement {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const sendRef = useRef<((text: string) => Promise<number | null>) | null>(null);
  const [isCommitting, setIsCommitting] = useState(false);
  const chat = state.createChats.find((item) => item.id === chatId);
  const nestChatId = chat?.aiChatId ?? null;
  const committed = Boolean(chat?.committedAt);
  const screen = createCompanyChatScreen();
  const messagesQuery = useGetAiChatMessagesQuery(
    nestChatId ? { chatId: nestChatId, limit: MESSAGES_LIMIT } : skipToken,
  );
  const livePending = pendingRowsFromAiChatMessages(messagesQuery.data || []);
  const pendingCount = livePending.length;
  const canCreate = Boolean(nestChatId) && !committed && !isCommitting && pendingCount > 0;

  const onCreateCompany = async () => {
    if (!nestChatId || !sendRef.current) {
      toast.error(t('targetedMarketing.chatBindFailed'));
      return;
    }
    setIsCommitting(true);
    try {
      await sendRef.current(CREATE_WATCH_COMPANIES_USER_TURN);
      const latest = (await messagesQuery.refetch()).data || [];
      const output = lastCreateWatchCompaniesOutput(latest);
      const kind = createWatchCompaniesCommitKind(output);
      if (kind === 'invalid') {
        toast.error(t('targetedMarketing.createChatInvalid'));
        return;
      }
      if (kind === 'all_skipped') {
        toast.error(t('targetedMarketing.createChatAllSkipped'));
        return;
      }
      if (kind === 'none') {
        toast.error(t('targetedMarketing.liveRequestFailed'));
        return;
      }
      dispatch(watchTowerApi.util.invalidateTags([
        WATCH_TOWER_COMPANIES_TAG,
        WATCH_TOWER_COMPANY_TAG,
        WATCH_TOWER_RUNS_TAG,
      ]));
      onApply({
        ...state,
        createChats: state.createChats.map((item) => (
          item.id === chatId
            ? { ...item, committedAt: new Date().toISOString() }
            : item
        )),
      });
      const createdCount = output?.created?.length || pendingCount;
      toast.success(t('targetedMarketing.createChatCreated', { count: createdCount }));
      onClose();
    } catch {
      toast.error(t('targetedMarketing.liveRequestFailed'));
    } finally {
      setIsCommitting(false);
    }
  };

  return (
    <WatchAiChatDialog
      open={open}
      title={chat?.title || t('targetedMarketing.newCompany')}
      description={t('targetedMarketing.createChatHintLive')}
      nestChatId={nestChatId}
      screen={screen}
      sendRef={sendRef}
      onClose={onClose}
      onChatCreated={(aiChatId) => {
        onApply(bindCreateChatAiChatId(state, chatId, aiChatId));
        toast.success(t('targetedMarketing.chatBound'));
      }}
      contextSlot={null}
      extraSlot={(
        <>
          <h3 className={styles.sectionTitle}>{t('targetedMarketing.createChatPendingTitle')}</h3>
          {pendingCount === 0 ? (
            <EmptyList text={t('targetedMarketing.createChatPendingEmptyLive')} />
          ) : (
            <div className={styles.tableWrap}>
              <table className={`${styles.table} ${styles.tableCompact}`}>
                <thead>
                  <tr>
                    <th>{t('targetedMarketing.name')}</th>
                    <th>{t('targetedMarketing.country')}</th>
                    <th>{t('targetedMarketing.products')}</th>
                    <th>{t('targetedMarketing.aiStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {livePending.map((row) => (
                    <tr key={row.uid || row.name}>
                      <td>{row.name}</td>
                      <td>{row.country}</td>
                      <td>{row.product}</td>
                      <td>
                        {row.skipUid
                          ? t('targetedMarketing.createChatPendingSkip', { uid: row.skipUid })
                          : t('targetedMarketing.createChatPendingNew')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className={styles.footerActions}>
            <Button
              title={t('targetedMarketing.createCompany')}
              variant={BUTTON_VARIANT.primary}
              disabled={!canCreate}
              onClick={onCreateCompany}
            />
          </div>
        </>
      )}
    />
  );
}

export default WatchTowerCreateChatDialog;
